import { useEffect, useRef, useState } from "react";
import { GraduationCap, Loader2, Send, Copy, ClipboardCheck, RotateCcw, Check, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { streamPost } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { emptyNote } from "@/lib/soap";
import { parseCoachFixes } from "@/lib/coachFixes";

const QUICK = [
  "How can I make my Objective section more measurable?",
  "Is medical necessity clear enough for insurance?",
  "What's missing for this service code?",
  "Rewrite my Assessment in clinical language",
];

const FIELDS = Object.keys(emptyNote());
const notePayload = (form) => Object.fromEntries(FIELDS.map((k) => [k, form[k]]));

function Line({ line, fix, idx, applied, onApply, streaming }) {
  if (!fix) return <div>{line || "\u00A0"}</div>;
  const done = applied.has(idx);
  return (
    <div className="my-1.5 rounded-lg border border-primary/20 bg-white p-2.5 flex items-start gap-2">
      <span className="flex-1 text-slate-800">{line.trim()}</span>
      <Button
        size="sm"
        variant={done ? "ghost" : "default"}
        className="h-7 shrink-0 rounded-full px-3 text-xs"
        disabled={streaming || done || (!fix.section && !fix.find)}
        onClick={() => onApply(fix, idx)}
        data-testid={`coach-apply-${idx}`}
      >
        {done ? <><Check className="h-3 w-3 mr-1" />Applied</> : <><Wand2 className="h-3 w-3 mr-1" />Apply</>}
      </Button>
    </div>
  );
}

function Bubble({ m, i, streaming, onApply }) {
  const coach = m.role === "coach";
  const [applied, setApplied] = useState(new Set());
  const fixes = coach && !streaming ? parseCoachFixes(m.content) : {};
  const apply = (fix, idx) => { if (onApply(fix)) setApplied((s) => new Set(s).add(idx)); };
  return (
    <div className={`flex ${coach ? "justify-start" : "justify-end"}`} data-testid={`coach-message-${i}`}>
      <div className={`max-w-[92%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${coach ? "bg-secondary/60 text-slate-800 rounded-tl-sm" : "bg-primary text-white rounded-tr-sm"}`}>
        {!m.content && streaming && <span className="inline-flex items-center gap-2 text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" />Coach is reading your note…</span>}
        {coach && m.content
          ? m.content.split("\n").map((line, idx) => (
            <Line key={idx} line={line} idx={idx} fix={fixes[idx]} applied={applied} onApply={apply} streaming={streaming} />
          ))
          : m.content}
        {coach && m.content && !streaming && (
          <button onClick={() => copyText(m.content, "Coach feedback copied")} className="mt-2 flex items-center gap-1 text-xs text-slate-500 hover:text-primary" data-testid={`coach-copy-${i}`}>
            <Copy className="h-3 w-3" />Copy
          </button>
        )}
      </div>
    </div>
  );
}

export function CoachPanel({ form, disabled, onApply }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages]);

  const ask = async (mode, text) => {
    const before = messages;
    const history = [...before, { role: "user", content: mode === "chat" ? text : "Review my note" }];
    setMessages([...history, { role: "coach", content: "" }]);
    setInput("");
    setBusy(true);
    try {
      await streamPost("/ai/coach", { note: notePayload(form), mode, messages: history.slice(-12) }, (t) =>
        setMessages([...history, { role: "coach", content: t.replace(/\*\*/g, "") }]));
    } catch (e) {
      toast.error(e.message);
      setMessages(before);
      if (mode === "chat") setInput(text);
    } finally { setBusy(false); }
  };

  const send = (e) => {
    e?.preventDefault();
    if (input.trim() && !busy) ask("chat", input.trim());
  };

  return (
    <>
      <Button variant="outline" className="rounded-full" onClick={() => setOpen(true)} disabled={disabled} data-testid="coach-open-button">
        <GraduationCap className="h-4 w-4 mr-2 text-primary" />BCBA Coach
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-xl flex flex-col p-0" data-testid="coach-panel">
          <SheetHeader className="px-6 pt-6 pb-4 border-b text-left">
            <SheetTitle className="flex items-center gap-2"><GraduationCap className="h-5 w-5 text-primary" />BCBA Coach</SheetTitle>
            <SheetDescription>Feedback on this note like a supervising BCBA would give — what's strong, what to fix, and what an auditor looks for.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-testid="coach-messages">
            {messages.length === 0 && (
              <div className="rounded-xl border border-dashed p-6 text-center space-y-4" data-testid="coach-empty-state">
                <ClipboardCheck className="h-8 w-8 mx-auto text-primary" />
                <p className="text-sm text-slate-600">Get a score out of 10, specific fixes with rewritten sentences, and an insurance audit checklist.</p>
                <Button className="rounded-full" onClick={() => ask("review")} disabled={busy} data-testid="coach-review-button">Review my note</Button>
              </div>
            )}
            {messages.map((m, i) => <Bubble key={i} m={m} i={i} streaming={busy && i === messages.length - 1} onApply={onApply} />)}
            <div ref={endRef} />
          </div>
          <div className="border-t px-6 py-4 space-y-3 bg-white">
            <div className="flex flex-wrap gap-2">
              {messages.length > 0 && (
                <button onClick={() => ask("review")} disabled={busy} className="rounded-full border px-3 py-1 text-xs text-primary hover:bg-secondary disabled:opacity-50 flex items-center gap-1" data-testid="coach-rereview-button">
                  <RotateCcw className="h-3 w-3" />Review again
                </button>
              )}
              {QUICK.map((q, i) => (
                <button key={q} onClick={() => ask("chat", q)} disabled={busy} className="rounded-full border px-3 py-1 text-xs text-slate-600 hover:border-primary hover:text-primary disabled:opacity-50" data-testid={`coach-quick-${i}`}>{q}</button>
              ))}
            </div>
            <form onSubmit={send} className="flex gap-2">
              <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={2} placeholder="Ask your coach anything about this note…"
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }} className="resize-none" data-testid="coach-input" />
              <Button type="submit" size="icon" className="h-auto w-12 rounded-xl" disabled={busy || !input.trim()} data-testid="coach-send-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </form>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
